import React, { useEffect, useState } from 'react'
import styles from '../CreateTripActivityPage/CreateTripActivityPage.module.scss'
import { Grid } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import tripService from '../../../supabaseServices/TripService'
import locationService from '../../../supabaseServices/LocationService'
import authService from '../../../supabaseServices/AuthService'
import { setSnackbar } from '../../../store/snackbar/actions'
import EditTripForm, { EditTripFormValues } from './EditTripForm'

interface TripData {
  id: number
  uuid: string
  trip_name: string
  location: string
  location_uuid: string | null
  start_date: string
  end_date: string
  locations?: {
    uuid: string
    latitude: string
    longitude: string
    name: string
    address: string
  }
}

const EditTripPage: React.FC = () => {
  const dispatch = useDispatch()
  const { uuid } = useParams<{ uuid: string }>()
  const [tripData, setTripData] = useState<TripData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTripData = async () => {
      try {
        setLoading(true)
        const trip = await tripService.getTripByUUID(uuid)
        if (trip) {
          setTripData(trip)
        }
      } catch (error) {
        console.error('Error fetching trip data:', error)
        dispatch(
          setSnackbar({
            open: true,
            severity: 'error',
            message: 'Failed to load trip data',
          }),
        )
      } finally {
        setLoading(false)
      }
    }

    fetchTripData()
  }, [uuid, dispatch])

  const onSubmit = async (values: EditTripFormValues) => {
    try {
      const profile = await authService.getUserProfile()

      let locationUUID: string | undefined = tripData?.location_uuid || undefined
      let locationName: string = typeof values.location === 'string' ? values.location : ''

      // Check if location is a new Google Places selection (object with place details)
      if (values.location && typeof values.location === 'object' && values.location.place_id) {
        // Create a new location entry
        locationUUID = await locationService.createLocation({
          latitude: values.location.lat,
          longitude: values.location.lng,
          address: values.location.formatted_address || values.location.description,
          google_place_id: values.location.place_id,
          created_by: profile.uuid,
        })
        locationName = values.location.formatted_address || values.location.description || ''
      } else if (typeof values.location === 'object' && values.location.name) {
        // Existing location object (already in system)
        locationName = values.location.name || values.location.address || ''
      }

      await tripService.updateTrip(uuid, {
        trip_name: values.trip_name,
        location: locationName,
        location_uuid: locationUUID,
        start_date: values.start_date?.toISOString(),
        end_date: values.end_date?.toISOString(),
        updated_by: profile.uuid,
      })

      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Trip updated successfully!',
        }),
      )
    } catch (error) {
      console.error('Error updating trip:', error)
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Failed to update trip',
        }),
      )
      throw error
    }
  }

  if (loading) {
    return <p>Loading...</p>
  }

  if (!tripData) {
    return <p>Trip not found</p>
  }

  const initialValues: EditTripFormValues = {
    trip_name: tripData.trip_name || '',
    location: tripData.locations || tripData.location || '',
    location_uuid: tripData.location_uuid || undefined,
    start_date: tripData.start_date ? new Date(tripData.start_date) : null,
    end_date: tripData.end_date ? new Date(tripData.end_date) : null,
  }

  return (
    <main>
      <Grid container spacing={0} py={2}>
        <Grid item xs={12}>
          <div className={styles.mainWrapper}>
            <div className={styles.headerWrapper}>
              <h1>Edit Trip</h1>
            </div>
            <EditTripForm onSubmit={onSubmit} initialValues={initialValues} />
          </div>
        </Grid>
      </Grid>
    </main>
  )
}

export default EditTripPage
